import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useRef } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import { MessageSquare, Send, Plus, Trash2, Clock, Loader2, Shield, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

export default function PharmaChatPage() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const loadSessions = async () => { try { const r = await axios.get(`${API}/pharmatech/chat/sessions`, { headers: getHeaders() }); setSessions(r.data.sessions || []); } catch {} };
  const loadHistory = async (sid) => { try { const r = await axios.get(`${API}/pharmatech/chat/history/${sid}`, { headers: getHeaders() }); setMessages(r.data.messages || []); setActiveSession(sid); } catch { toast.error("Erreur chargement"); } };
  const newChat = () => { setActiveSession(null); setMessages([]); setInput(""); };
  const deleteSession = async (sid, e) => { e.stopPropagation(); try { await axios.delete(`${API}/pharmatech/chat/session/${sid}`, { headers: getHeaders() }); setSessions(s => s.filter(x => x.session_id !== sid)); if (activeSession === sid) newChat(); toast.success("Supprime"); } catch {} };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim(); setInput("");
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/pharmatech/chat`, { message: text, session_id: activeSession || undefined }, { headers: getHeaders() });
      if (!activeSession) setActiveSession(r.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: r.data.response, timestamp: new Date().toISOString() }]);
      loadSessions();
    } catch (e) { toast.error("Erreur: " + (e.response?.data?.detail || e.message)); setMessages(prev => prev.slice(0, -1)); setInput(text); }
    finally { setLoading(false); }
  };

  const suggestions = [
    "Quelles sont les interactions entre ibuprofene et anticoagulants ?",
    "Quelle alternative au metronidazole chez la femme enceinte ?",
    "Posologie pediatrique de l'amoxicilline pour un enfant de 15kg ?",
    "Quels antibiotiques eviter en cas d'insuffisance renale ?",
  ];

  return (
    <PharmatechLayout title="PharmaGPT" subtitle="Assistant pharmaceutique intelligent">
      <div data-testid="pharma-chat-page" className="flex gap-4 h-[calc(100vh-140px)]">
        {/* Sessions sidebar */}
        <div className="w-64 flex-shrink-0 flex flex-col rounded-xl overflow-hidden bg-white border border-slate-200 shadow-sm hidden md:flex">
          <div className="p-3 border-b border-slate-100">
            <Button onClick={newChat} className="w-full text-xs gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-chat-btn">
              <Plus className="w-3.5 h-3.5" /> Nouvelle conversation
            </Button>
          </div>
          <div className="flex-1 overflow-auto p-2 space-y-1">
            {sessions.map(s => (
              <div key={s.session_id} onClick={() => loadHistory(s.session_id)} data-testid={`session-${s.session_id}`}
                className={`w-full flex items-start gap-2 p-2.5 rounded-lg text-left transition-all group cursor-pointer ${activeSession === s.session_id ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50"}`}>
                <MessageSquare className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${activeSession === s.session_id ? "text-blue-500" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700 truncate">{s.preview}</p>
                  <p className="text-[9px] text-slate-400 mt-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{new Date(s.timestamp).toLocaleDateString("fr-FR")}</p>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {sessions.length === 0 && <p className="text-center text-[10px] text-slate-400 py-8">Aucune conversation</p>}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col rounded-xl overflow-hidden bg-white border border-slate-200 shadow-sm">
          <div className="flex-1 overflow-auto p-4 space-y-4">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-gradient-to-br from-blue-500 to-cyan-500">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">PharmaGPT CMES</h3>
                <p className="text-xs text-slate-500 max-w-md mb-6">Assistant IA specialise en pharmacologie africaine. Posez vos questions cliniques.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} data-testid={`suggestion-${i}`}
                      className="text-left p-3 rounded-lg text-xs border border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50 transition-all">
                      <ChevronRight className="w-3 h-3 inline mr-1 text-blue-500" />{s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] p-3.5 rounded-2xl ${msg.role === "user" ? "rounded-br-sm bg-blue-600 text-white" : "rounded-bl-sm bg-slate-100 text-slate-700 border border-slate-200"}`}>
                  <p className="text-xs leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="p-3.5 rounded-2xl rounded-bl-sm bg-slate-100 border border-slate-200">
                  <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /><span className="text-xs text-slate-500">PharmaGPT reflechit...</span></div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="px-4 py-1.5 bg-amber-50 border-t border-amber-100">
            <p className="text-[9px] text-center text-amber-700"><Shield className="w-2.5 h-2.5 inline mr-1" />Information a titre d'orientation uniquement - Ne se substitue pas a l'expertise du pharmacien</p>
          </div>

          <div className="p-3 border-t border-slate-100">
            <div className="flex gap-2">
              <input data-testid="chat-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
                className="flex-1 px-4 py-3 rounded-xl text-sm border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-400 text-slate-800 placeholder-slate-400"
                placeholder="Posez votre question pharmaceutique..." disabled={loading} />
              <Button onClick={sendMessage} disabled={loading || !input.trim()} data-testid="send-chat-btn" className="px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PharmatechLayout>
  );
}
